/**
 * A proposal says where it points, on the canvas.
 *
 * A proposal chip names a claim by its label, in a thread beside a graph of
 * dozens of them, and nothing tied the two: a reader weighing "Diverge “Weight
 * reduction is the live target”" had to find that claim among the captions —
 * or among the dots, where the canvas leaves a caption out to keep the rest
 * legible. The design report's "the question pushes back" asks for the first
 * reframe, the voice's first proposal, to be seen where it lands on the map.
 *
 * So two moments light the claim a proposal is about, with marks that already
 * exist and mean "this one":
 *
 *  - when the worked example's authored line lands with its two chips, its
 *    FIRST proposal's claim takes one pulse — the same 1.5 s glow a pass leaves
 *    on what it touched. At the transition only (`narrator.ts` calls this as
 *    the row lands), never for a row a reload finds lying there, the
 *    discipline every voice trigger keeps. It is the one voice row with chips:
 *    the model's own voice names no next move (D295);
 *  - while a chip is hovered or focused, its claim wears the hover ring and its
 *    caption is drawn (`hover`, the canvas's own), and takes one pulse as the
 *    chip is reached.
 *
 * Under reduced motion the pulse draws nothing (`pulseLevel`); the ring is
 * still, so it is what a chip says there. Nothing here moves the camera — a
 * hover is not a pick (invariant 11), and a claim off the field stays there —
 * and nothing here spends: a mark is not a press (invariant 9).
 */

import { pulses } from '../core/pulse'
import { useStore } from '../core/store'
import { requestFrame } from '../canvas/frameBus'

/** One pulse on a claim, and a frame to start drawing it: a mark on the clock
 *  writes no state, so it wakes a resting loop by name. */
export function pulseClaim(nodeId: string): void {
  pulses.mark([nodeId])
  requestFrame()
}

/**
 * The narrator's proposal row has just appeared on `sessionId`: one pulse on the
 * claim its first proposal names — the first a chip can render for, since a
 * proposal on a claim that has left the graph renders no chip.
 */
export function pulseFirstProposal(
  sessionId: string,
  acts: ReadonlyArray<{ node: string }> | undefined,
): void {
  const s = useStore.getState().session
  if (s.id !== sessionId || !acts?.length) return
  const first = acts.find((a) => s.nodes[a.node] !== undefined)
  if (first) pulseClaim(first.node)
}

/** A chip reached, by the pointer or by focus: ring its claim and pulse it once. */
export function pointAtClaim(nodeId: string): void {
  useStore.getState().hover(nodeId)
  pulseClaim(nodeId)
}

/** The chip let go: its ring comes off, unless something else has taken the hover. */
export function releaseClaim(nodeId: string): void {
  const st = useStore.getState()
  if (st.hoverId === nodeId) st.hover(null)
}
